import { useMemo } from 'react'
import { motion } from 'framer-motion'
import {
  TrendingUp,
  TrendingDown,
  Users,
  Briefcase,
  Clock,
  Target,
  BarChart3,
  PieChart as PieChartIcon,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { cn } from '@/lib/utils'

// Stat Card Component
interface StatCardProps {
  title: string
  value: string | number
  change?: number
  changeLabel?: string
  icon: typeof Users
  trend?: 'up' | 'down' | 'neutral'
}

export function StatCard({
  title,
  value,
  change,
  changeLabel,
  icon: Icon,
  trend,
}: StatCardProps) {
  return (
    <Card className="h-full">
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground truncate">{title}</p>
            <p className="text-2xl font-bold mt-0.5">{value}</p>
            {change !== undefined && (
              <div className="flex items-center gap-1 mt-1">
                {trend === 'up' ? (
                  <TrendingUp className="h-3 w-3 text-success" />
                ) : trend === 'down' ? (
                  <TrendingDown className="h-3 w-3 text-destructive" />
                ) : null}
                <span
                  className={`text-xs font-medium ${
                    trend === 'up'
                      ? 'text-success'
                      : trend === 'down'
                      ? 'text-destructive'
                      : 'text-muted-foreground'
                  }`}
                >
                  {change > 0 ? '+' : ''}
                  {change}%
                </span>
                {changeLabel && (
                  <span className="text-xs text-muted-foreground">{changeLabel}</span>
                )}
              </div>
            )}
          </div>
          <div className="p-2 rounded-full bg-primary/10 flex-shrink-0">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Pipeline Funnel Chart
interface PipelineData {
  stage: string
  count: number
  color: string
}

export function PipelineFunnelChart({ data, className }: { data: PipelineData[]; className?: string }) {
  const maxCount = data.length > 0 ? Math.max(...data.map((d) => d.count)) : 0

  return (
    <Card className={cn('h-full flex flex-col', className)}>
      <CardHeader className="flex-shrink-0 py-3 px-4">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Target className="h-4 w-4" />
          Pipeline Funnel
        </CardTitle>
        <CardDescription className="text-xs">Candidate progression through stages</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 overflow-y-auto px-4 pb-3 pt-0">
        {data.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <Target className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No pipeline data yet</p>
          </div>
        ) : (
        <div className="space-y-2">
          {data.map((item, index) => {
            const width = maxCount > 0 ? (item.count / maxCount) * 100 : 0
            const conversionRate =
              index > 0 && data[index - 1].count > 0
                ? ((item.count / data[index - 1].count) * 100).toFixed(0)
                : null

            return (
              <div key={item.stage} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium">{item.stage}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{item.count}</span>
                    {conversionRate && (
                      <span className="text-[10px] text-muted-foreground">
                        ({conversionRate}%)
                      </span>
                    )}
                  </div>
                </div>
                <div className="h-6 bg-muted rounded-lg overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${width}%` }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    className="h-full rounded-lg"
                    style={{ backgroundColor: item.color }}
                  />
                </div>
              </div>
            )
          })}
        </div>
        )}
      </CardContent>
    </Card>
  )
}

// Source Effectiveness Chart
interface SourceData {
  source: string
  applications: number
  hires: number
  conversionRate: number
}

export function SourceEffectivenessChart({ data, className }: { data: SourceData[]; className?: string }) {
  const sortedData = [...data].sort((a, b) => b.applications - a.applications)
  const maxApplications = data.length > 0 ? Math.max(...data.map((d) => d.applications)) : 0

  return (
    <Card className={cn('h-full flex flex-col', className)}>
      <CardHeader className="flex-shrink-0 py-3 px-4">
        <CardTitle className="flex items-center gap-2 text-sm">
          <BarChart3 className="h-4 w-4" />
          Source Effectiveness
        </CardTitle>
        <CardDescription className="text-xs">Where your candidates come from</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 overflow-y-auto px-4 pb-3 pt-0">
        {data.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <BarChart3 className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No source data yet</p>
          </div>
        ) : (
        <div className="space-y-3">
          {sortedData.map((item) => {
            const width = maxApplications > 0 ? (item.applications / maxApplications) * 100 : 0

            return (
              <div key={item.source} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium">{item.source}</span>
                  <span className="text-muted-foreground">
                    {item.applications} apps • {item.hires} hires
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${width}%` }}
                      transition={{ duration: 0.5 }}
                      className="h-full bg-primary rounded-full"
                    />
                  </div>
                  <span className="text-xs font-medium w-12 text-right">
                    {item.conversionRate.toFixed(1)}%
                  </span>
                </div>
              </div>
            )
          })}
        </div>
        )}
      </CardContent>
    </Card>
  )
}

// Time to Hire Chart
interface TimeToHireData {
  month: string
  days: number
}

export function TimeToHireChart({ data, className }: { data: TimeToHireData[]; className?: string }) {
  const maxDays = data.length > 0 ? Math.max(...data.map((d) => d.days)) : 0
  const avgDays = data.length > 0 ? data.reduce((sum, d) => sum + d.days, 0) / data.length : 0

  return (
    <Card className={cn('h-full flex flex-col', className)}>
      <CardHeader className="flex-shrink-0 py-3 px-4">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4" />
          Time to Hire
        </CardTitle>
        <CardDescription className="text-xs">Avg days to hire</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 flex flex-col px-4 pb-3 pt-0">
        {data.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <Clock className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No hire data yet</p>
          </div>
        ) : (
        <>
        <div className="mb-2 flex-shrink-0">
          <span className="text-2xl font-bold">{avgDays.toFixed(0)}</span>
          <span className="text-muted-foreground text-xs ml-1">days avg</span>
        </div>
        <div className="flex items-end gap-1 flex-1 min-h-0">
          {data.map((item, index) => {
            const height = maxDays > 0 ? (item.days / maxDays) * 100 : 0

            return (
              <div key={item.month} className="flex-1 flex flex-col items-center gap-0.5 h-full justify-end">
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${height}%` }}
                  transition={{ duration: 0.5, delay: index * 0.03 }}
                  className="w-full bg-primary/80 rounded-t-sm min-h-[2px]"
                  title={`${item.days} days`}
                />
                <span className="text-[8px] text-muted-foreground leading-none">{item.month.slice(0, 3)}</span>
              </div>
            )
          })}
        </div>
        </>
        )}
      </CardContent>
    </Card>
  )
}

// Applications Over Time Chart
interface ApplicationsOverTimeData {
  date: string
  count: number
}

export function ApplicationsOverTimeChart({ data, className }: { data: ApplicationsOverTimeData[]; className?: string }) {
  const maxCount = data.length > 0 ? Math.max(...data.map((d) => d.count)) : 0
  const totalApplications = data.reduce((sum, d) => sum + d.count, 0)

  // Create a simple line path
  const points = data.length > 1 ? data.map((d, i) => ({
    x: (i / (data.length - 1)) * 100,
    y: maxCount > 0 ? 100 - (d.count / maxCount) * 100 : 50,
  })) : []

  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
    .join(' ')

  return (
    <Card className={cn('h-full flex flex-col', className)}>
      <CardHeader className="flex-shrink-0 py-3 px-4">
        <CardTitle className="flex items-center gap-2 text-sm">
          <TrendingUp className="h-4 w-4" />
          Applications Over Time
        </CardTitle>
        <CardDescription className="text-xs">{totalApplications} total applications</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 overflow-hidden px-4 pb-3 pt-0">
        {data.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <TrendingUp className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No application data yet</p>
          </div>
        ) : (
        <div className="h-full flex flex-col">
          <div className="flex-1 min-h-0 relative">
            <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
              {/* Grid lines */}
              {[0, 25, 50, 75, 100].map((y) => (
                <line
                  key={y}
                  x1="0"
                  y1={y}
                  x2="100"
                  y2={y}
                  stroke="currentColor"
                  strokeOpacity="0.1"
                  strokeWidth="0.5"
                />
              ))}
              {/* Area fill */}
              <motion.path
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                d={`${pathD} L 100 100 L 0 100 Z`}
                fill="url(#gradient)"
                fillOpacity="0.3"
              />
              {/* Line */}
              <motion.path
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1 }}
                d={pathD}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Gradient definition */}
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="hsl(var(--primary))" />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          {/* X-axis labels */}
          <div className="flex justify-between mt-1 text-[10px] text-muted-foreground flex-shrink-0">
            {data.filter((_, i) => i % Math.ceil(data.length / 5) === 0).map((item) => (
              <span key={item.date}>{item.date}</span>
            ))}
          </div>
        </div>
        )}
      </CardContent>
    </Card>
  )
}

// Job Performance Summary
interface JobPerformance {
  id: string
  title: string
  applications: number
  interviews: number
  offers: number
  hires: number
  status: 'published' | 'draft' | 'closed'
}

export function JobPerformanceSummary({ jobs }: { jobs: JobPerformance[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Briefcase className="h-5 w-5" />
          Job Performance
        </CardTitle>
        <CardDescription>Performance metrics by job posting</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {jobs.slice(0, 5).map((job) => (
            <div key={job.id} className="flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{job.title}</p>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <span>{job.applications} applied</span>
                  <span>{job.interviews} interviewed</span>
                  <span>{job.hires} hired</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {/* Mini funnel visualization */}
                {[job.applications, job.interviews, job.offers, job.hires].map((count, i) => {
                  const maxCount = job.applications || 1
                  const height = (count / maxCount) * 24 + 4
                  return (
                    <div
                      key={i}
                      className="w-2 bg-primary/20 rounded-sm flex items-end"
                      style={{ height: 28 }}
                    >
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height }}
                        className="w-full bg-primary rounded-sm"
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
